import { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import styles from "./Calendar.module.scss";

const FullCalendar = dynamic(() => import('@fullcalendar/react'), { ssr: false });

const loadPlugins = async () => {
  const dayGridPlugin = (await import('@fullcalendar/daygrid')).default;
  const timeGridPlugin = (await import('@fullcalendar/timegrid')).default;
  const interactionPlugin = (await import('@fullcalendar/interaction')).default;
  return [dayGridPlugin, timeGridPlugin, interactionPlugin];
};

export default function Calendar({ specials, sanityEvents }) {
  const [events, setEvents] = useState([]);
  const [plugins, setPlugins] = useState([]);

  useEffect(() => {
    // Load plugins dynamically
    loadPlugins().then(setPlugins);
  }, []);

  useEffect(() => {
    const mapEvents = (items, mapper) => items?.map(mapper) || [];

    // Filter specials by their type and map accordingly
    const mappedProductSpecials = mapEvents(
      specials?.filter(special => special.special_type === "product"),
      item => ({
        title: item.title,
        start: item.start_date,
        end: item.end_date,
        url: `/shop?special=${item.id}`,
        classNames: `${styles.productItem}`,
      })
    );

    const mappedBundleSpecials = mapEvents(
      specials?.filter(special => special.special_type === "bundle"),
      item => ({
        title: item.title,
        start: item.start_date,
        end: item.end_date,
        url: `/deals/bundle/${item.id}`,
        classNames: `${styles.bundleItem}`,
      })
    );

    const mappedSanityEvents = mapEvents(sanityEvents, event => ({
      title: event.title,
      start: event.startDate,
      end: event.endDate,
      url: event.slug?.current ? `/events/${event.slug.current}` : null,
      classNames: `${styles.eventItem}`,
    }));

    // Combine all mapped events
    setEvents([...mappedSanityEvents, ...mappedProductSpecials, ...mappedBundleSpecials]);
  }, [specials, sanityEvents]);

  if (plugins.length === 0) {
    // Return a fallback UI until plugins are loaded
    return <div>Loading Calendar...</div>;
  }

  return (
    <>
      <div className={styles.legend}>
        {specials?.some(s => s.special_type === "product") && (
          <div className={styles.product}>Product Sale</div>
        )}
        {specials?.some(s => s.special_type === "bundle") && (
          <div className={styles.bundle}>Bundle Deal</div>
        )}
        {sanityEvents?.length > 0 && <div className={styles.event}>In-store Event</div>}
      </div>

      <FullCalendar
        plugins={plugins}
        headerToolbar={{
          left: 'prev,next',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay',
        }}
        initialView="dayGridMonth"
        height="auto"
        events={events}
        eventDisplay={'block'}
        eventOrder={'strict'}
        fixedWeekCount={false}
        dayMaxEventRows={7}
        themeSystem="pulse"
        dayPopoverFormat={{
          day: 'numeric', month: 'short', year: 'numeric', omitCommas: true,
        }}
        validRange={() => {
          const now = new Date();
          const start = new Date(now.getFullYear(), now.getMonth(), 1);
          const nextMonth = new Date(now.getFullYear(), now.getMonth() + 2, 0);
          const end = new Date(nextMonth.getFullYear(), nextMonth.getMonth(), nextMonth.getDate(), 23, 59, 59, 999);
          return { start, end };
        }}
      />
    </>
  );
}
